import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './LegalPage.css';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      <Helmet>
        <title>Privacy Policy | coparent</title>
        <meta name="description" content="How coparent collects, uses, and protects your family's data. We don't sell or share — your information stays between the two parents in your family." />
        <link rel="canonical" href="https://letscoparent.us/privacy" />
        <meta property="og:title" content="Privacy Policy | coparent" />
        <meta property="og:description" content="How coparent collects, uses, and protects your family's data. We don't sell or share." />
        <meta property="og:url" content="https://letscoparent.us/privacy" />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="legal-nav">
        <button className="legal-back" onClick={() => navigate('/')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
          Back to home
        </button>
      </div>

      <div className="legal-container">
        <h1 className="legal-title">Privacy Policy</h1>
        <p className="legal-updated">Last updated: March 27, 2026</p>

        <div className="legal-content">
          <section>
            <h2>1. Introduction</h2>
            <p>
              coparent ("we," "our," or "us") is committed to protecting the privacy of our users. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our web application and related services (collectively, the "Service").
            </p>
            <p>
              By using the Service, you agree to the collection and use of information in accordance with this policy. If you do not agree with the terms of this Privacy Policy, please do not access the Service.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            <h3>Account Information</h3>
            <p>When you create an account, we collect your name, email address, and profile photo (if you sign in with Google). This information is used to identify you within the app and to your co-parent.</p>

            <h3>Family and Custody Data</h3>
            <p>We collect information you provide about your family, including children's first names, custody schedules, activities, expenses, and messages exchanged with your co-parent. This data is essential to providing the Service.</p>

            <h3>Google Calendar Data</h3>
            <p>If you choose to connect Google Calendar, we request access to create and manage events in a dedicated "coparent" calendar within your Google account. We do not read or access any of your existing Google Calendar data. We only write events that you explicitly sync from the Service.</p>

            <h3>Usage Data</h3>
            <p>We may automatically collect certain information when you access the Service, including your browser type, device information, and pages visited. This data is used to improve the Service and is not linked to your identity.</p>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide, maintain, and improve the Service</li>
              <li>Create and manage your account</li>
              <li>Facilitate communication and data sharing between co-parents within a family</li>
              <li>Sync custody schedules and activities to your Google Calendar (if you opt in)</li>
              <li>Send you important service-related notifications</li>
              <li>Respond to your requests and provide customer support</li>
              <li>Monitor and analyze usage trends to improve the user experience</li>
            </ul>
          </section>

          <section>
            <h2>4. How We Share Your Information</h2>
            <p>We do not sell, trade, or rent your personal information to third parties. We may share your information in the following limited circumstances:</p>
            <ul>
              <li><strong>With your co-parent:</strong> Family data (schedules, expenses, activities, messages) is shared between members of the same family group within the Service. This is the core function of the app.</li>
              <li><strong>Service providers:</strong> We use Firebase (Google Cloud) to host and store your data. Google processes this data in accordance with their privacy policies and our data processing agreements.</li>
              <li><strong>Legal requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities.</li>
            </ul>
          </section>

          <section>
            <h2>5. Data Storage and Security</h2>
            <p>Your data is stored securely using Google Firebase, which provides encryption in transit and at rest. We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.</p>
            <p>While we strive to use commercially acceptable means to protect your personal information, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.</p>
          </section>

          <section>
            <h2>6. Google API Services</h2>
            <p>
              coparent's use and transfer of information received from Google APIs adheres to the <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a>, including the Limited Use requirements.
            </p>
            <p>Specifically, we only use Google Calendar access to:</p>
            <ul>
              <li>Create a dedicated "coparent" calendar in your Google account</li>
              <li>Add, update, and remove custody and activity events that you choose to sync</li>
            </ul>
            <p>We do not use Google data for advertising, and we do not transfer Google data to third parties except as necessary to provide the Service.</p>
          </section>

          <section>
            <h2>7. Your Rights and Choices</h2>
            <p>You have the following rights regarding your data:</p>
            <ul>
              <li><strong>Access and update:</strong> You can access and update your personal information through your account settings.</li>
              <li><strong>Delete your account:</strong> You may request deletion of your account and associated data by contacting us.</li>
              <li><strong>Disconnect Google Calendar:</strong> You can disconnect Google Calendar integration at any time from Settings, which will remove all synced events.</li>
              <li><strong>Revoke Google access:</strong> You can revoke our access to your Google account at any time through your <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">Google Account permissions</a>.</li>
            </ul>
          </section>

          <section>
            <h2>8. Children's Privacy</h2>
            <p>The Service is intended for use by parents and guardians. We collect children's first names only to identify custody arrangements and activities. We do not knowingly collect personal information directly from children under 13. If you believe we have inadvertently collected such information, please contact us immediately.</p>
          </section>

          <section>
            <h2>9. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date. Your continued use of the Service after any changes constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2>10. Contact Us</h2>
            <p>If you have questions or concerns about this Privacy Policy, please contact us at:</p>
            <p><strong>Email:</strong> privacy@coparent.app</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
